import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchFromOldApi } from '~/services/Http';
import { hasOneOfRoles } from '~/services/Permissions';

import { Roles } from '../user';

import { Employees } from './types';

export type EmployeesIndexed = {
  [key: number]: Employees;
};

export type StoreState = {
  activeEmployees: EmployeesIndexed;
  allEmployees: EmployeesIndexed;
  newEmployees: EmployeesIndexed;
  editableEndedEmployees: EmployeesIndexed;
  restrictedEndedEmployees: EmployeesIndexed;
  hasData: boolean;
  isLoading: boolean;
  failedToLoad: boolean;
};

const initialState: StoreState = {
  activeEmployees: {},
  allEmployees: {},
  newEmployees: {},
  editableEndedEmployees: {},
  restrictedEndedEmployees: {},
  hasData: false,
  isLoading: false,
  failedToLoad: false,
};

export enum EmployeesActionKeyPrefixes {
  FETCH_EMPLOYEES = 'employees/FETCH_EMPLOYEES',
}

export type EmployeesPayload = {
  activeEmployees: EmployeesIndexed;
  allEmployees: EmployeesIndexed;
  newEmployees: EmployeesIndexed;
  editableEndedEmployees: EmployeesIndexed;
  restrictedEndedEmployees: EmployeesIndexed;
};

type FetchEmployeesProps = {
  myRoles: string[];
};

export const getEmployees = (
  type?: string,
  restricted?: boolean,
): Promise<Employees[]> =>
  fetchFromOldApi(
    `/json/employee/full${type ? '/' + type : ''}?format=${
      type ? 'short' : 'SHORT'
    }${restricted ? '&restricted=true' : ''}`,
  ).then((r) => r.json());

export const getEditableEndedEmployees = (): Promise<Employees[]> =>
  fetchFromOldApi('/json/employee/ended/editable').then((r) => r.json());

export const fetchEmployees = createAsyncThunk<
  EmployeesPayload,
  FetchEmployeesProps
>(
  EmployeesActionKeyPrefixes.FETCH_EMPLOYEES,
  async ({ myRoles }): Promise<EmployeesPayload> => {
    const shouldLoad = {
      newEmployees: !hasOneOfRoles(myRoles, [
        Roles.DEPUTY_MANAGER,
        Roles.EMPLOYEE,
        Roles.GUEST,
        Roles.HR_INITIAL,
        Roles.RECRUITER,
      ]),
      pendingEmployees: hasOneOfRoles(myRoles, [
        Roles.HR_STAFF,
        Roles.HR_ADMIN,
        Roles.HR_ADMIN_LIMITED,
        Roles.HR_DEV,
        Roles.IT_ADMIN,
      ]),
      endedEmployees: hasOneOfRoles(myRoles, [
        Roles.HR_STAFF,
        Roles.HR_ADMIN,
        Roles.HR_ADMIN_LIMITED,
        Roles.HR_DEV,
        Roles.IT_ADMIN,
        Roles.HR_SALARY,
      ]),
      editableEndedEmployees: hasOneOfRoles(myRoles, [
        Roles.HR_STAFF,
        Roles.HR_ADMIN,
        Roles.HR_ADMIN_LIMITED,
        Roles.HR_DEV,
        Roles.IT_ADMIN,
        Roles.ALT_MANAGER,
        Roles.MANAGER,
      ]),
    };

    const mapEmployeesArrayToEmployeesIndexed = (employees: Employees[]) =>
      employees.reduce((acc, cur) => {
        acc[cur.fEmpId] = cur;
        return acc;
      }, {} as EmployeesIndexed);

    return Promise.all([
      getEmployees(),
      shouldLoad.newEmployees ? getEmployees('new', true) : Promise.resolve([]),
      shouldLoad.pendingEmployees
        ? getEmployees('pending')
        : Promise.resolve([]),
      shouldLoad.endedEmployees ? getEmployees('ended') : Promise.resolve([]),
      shouldLoad.editableEndedEmployees
        ? getEditableEndedEmployees()
        : Promise.resolve([]),
      shouldLoad.editableEndedEmployees
        ? getEmployees('ended', true)
        : Promise.resolve([]),
    ]).then(
      ([
        activeEmployees,
        newEmployees,
        pendingEmployees,
        endedEmployees,
        editableEndedEmployees,
        endedEmployeesRestricted,
      ]): EmployeesPayload => {
        const allEmployees = ([] as Employees[]).concat(
          activeEmployees,
          newEmployees,
          pendingEmployees,
          endedEmployees,
        );

        return {
          allEmployees: mapEmployeesArrayToEmployeesIndexed(allEmployees),
          activeEmployees: mapEmployeesArrayToEmployeesIndexed(activeEmployees),
          newEmployees: mapEmployeesArrayToEmployeesIndexed(newEmployees),
          editableEndedEmployees: mapEmployeesArrayToEmployeesIndexed(
            editableEndedEmployees,
          ),
          restrictedEndedEmployees: mapEmployeesArrayToEmployeesIndexed(
            endedEmployeesRestricted,
          ),
        };
      },
    );
  },
);

export const employeesSlice = createSlice({
  name: '~/data/employees',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchEmployees.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<EmployeesPayload>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.hasData = true;
      state.allEmployees = action.payload.allEmployees;
      state.activeEmployees = action.payload.activeEmployees;
      state.newEmployees = action.payload.newEmployees;
      state.editableEndedEmployees = action.payload.editableEndedEmployees;
      state.restrictedEndedEmployees = action.payload.restrictedEndedEmployees;
    },
    [fetchEmployees.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchEmployees.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const employeesReducer = employeesSlice.reducer;
